import { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import Button from '../components/Button';
import { fmtDT } from '../utils/helpers';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/Toast';
import type { Role } from '../types';
import { ROLES, ROLE_LABELS } from '../utils/constants';
import { useDepartments, invalidateDepartments } from '../hooks/useDepartments';
import { invalidateWorkStatuses } from '../hooks/useWorkStatuses';

// ── Types ──────────────────────────────────────────────────────────────────────

type Tab = 'users' | 'settings' | 'departments' | 'work-statuses' | 'forms' | 'custom-fields';

// This account's Admin + IT Manager roles are permanently protected server-side (see
// backend adminController.ts) — mirrored here only to disable the relevant controls in
// the UI; the backend is the actual source of truth and rejects the change either way.
const PROTECTED_ADMIN_EMAIL = 'kayum.khaja@permus.io';
const isProtectedAdminEmail = (email: string) => email.toLowerCase() === PROTECTED_ADMIN_EMAIL;

interface AdminUser {
  id: string; name: string; username: string; email: string;
  role: Role; userRoles?: { role: Role }[];
  department: string | null; jobTitle: string | null;
  managerId: string | null;
  isActive: boolean; azureId: string | null; lastLogin: string | null;
  updatedAt: string; createdAt: string;
}

interface LineManager { id: string; name: string; email: string; department: string | null; }
interface UserPage { users: AdminUser[]; total: number; page: number; limit: number; }

interface Setting  { key: string; value: string; description?: string; }

interface Dept {
  id: string; name: string; code: string; managerId: string | null;
  isActive: boolean; userCount: number; createdAt: string;
}

interface FormReg {
  id: string; formCode: string; formName: string; prefix: string;
  seqStart: number; seqCurrent: number; ticketCount: number;
  description?: string; isActive: boolean; createdAt: string;
}

interface AuditLogEntry {
  id: string; action: string; resourceType: string | null; resourceId: string | null;
  newValue: any; ipAddress: string | null; createdAt: string;
  user?: { id: string; name: string; email: string } | null;
}
interface AuditPage { entries: AuditLogEntry[]; total: number; page: number; limit: number; }

// ── Helpers ───────────────────────────────────────────────────────────────────

function initials(name: string): string {
  return name.split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() ?? '').join('');
}

function avatarColor(role: Role): string {
  const map: Record<Role, string> = {
    employee:       'bg-gray-100 text-gray-600',
    line_manager:   'bg-blue-100 text-blue-700',
    it_staff:       'bg-violet-100 text-violet-700',
    it_manager:     'bg-emerald-100 text-emerald-700',
    top_management: 'bg-amber-100 text-amber-700',
    admin:          'bg-[#010E23] text-white',
  };
  return map[role] ?? 'bg-gray-100 text-gray-600';
}

function exportUsersCsv(users: AdminUser[]) {
  const headers = ['Name', 'Username', 'Email', 'Role', 'Department', 'Job Title', 'M365 Status', 'Last Login'];
  const rows = users.map(u => [
    u.name, u.username, u.email, ROLE_LABELS[u.role],
    u.department ?? '', u.jobTitle ?? '',
    !u.azureId ? 'Manual' : u.isActive ? 'Active' : 'Disabled',
    u.lastLogin ? fmtDT(u.lastLogin) : '',
  ]);
  const csv = [headers, ...rows]
    .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = Object.assign(document.createElement('a'), { href: url, download: 'permus-users.csv' });
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Modal ─────────────────────────────────────────────────────────────────────

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold text-[#010E23]">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

const inp = 'w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-violet-600';
const sel = inp + ' bg-white';

// ── UsersPanel ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 50;

function UsersPanel({ currentUserId }: { currentUserId: string }) {
  const { toast } = useToast();

  const [search,       setSearch]       = useState('');
  const [roleFilter,   setRoleFilter]   = useState('');
  const [deptFilter,   setDeptFilter]   = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page,         setPage]         = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [data,    setData]    = useState<UserPage | null>(null);
  const [loading, setLoading] = useState(false);

  const [pendingRoles, setPendingRoles] = useState<Record<string, Role>>({});
  const [savingId,     setSavingId]     = useState<string | null>(null);
  const [saveErrors,   setSaveErrors]   = useState<Record<string, string>>({});

  const [selectedIds,  setSelectedIds]  = useState<Set<string>>(new Set());
  const [bulkRole,     setBulkRole]     = useState<Role | ''>('');
  const [applyingBulk, setApplyingBulk] = useState(false);

  const [exporting,   setExporting]   = useState(false);
  const [togglingId,  setTogglingId]  = useState<string | null>(null);

  // Create User modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '', username: '', email: '', password: '', role: 'employee' as Role, department: '', jobTitle: '',
  });
  const [creating, setCreating] = useState(false);

  // Edit User modal
  const [editUser,  setEditUser]  = useState<AdminUser | null>(null);
  const [editForm,  setEditForm]  = useState({ name: '', department: '', jobTitle: '', managerId: '' });
  const [editing,   setEditing]   = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPw, setResettingPw] = useState(false);

  // Manage Roles modal
  const [rolesUser,       setRolesUser]       = useState<AdminUser | null>(null);
  const [pendingRoleSet,  setPendingRoleSet]  = useState<Set<Role>>(new Set());
  const [savingRoles,     setSavingRoles]     = useState(false);

  // Live department list for dropdowns (shared cache — reflects newly-added depts)
  const { names: departmentNames } = useDepartments();

  // Line managers list (for LM assignment dropdown). Refetched on mount and when
  // the edit modal opens, so a newly-created line manager appears without a reload.
  const [lineManagers, setLineManagers] = useState<LineManager[]>([]);
  const loadLineManagers = useCallback(() => {
    api.get<LineManager[]>('/admin/users/line-managers')
      .then(r => setLineManagers(r.data))
      .catch(() => {});
  }, []);
  useEffect(() => { loadLineManagers(); }, [loadLineManagers]);

  // Detect whether a dedicated IT Manager exists — admin shows a fallback badge when none does
  const [noITManager, setNoITManager] = useState(false);
  useEffect(() => {
    api.get<UserPage>('/admin/users?role=it_manager&limit=1&page=1')
      .then(r => setNoITManager(r.data.total === 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [debouncedSearch, roleFilter, deptFilter, statusFilter]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (roleFilter)      params.set('role',   roleFilter);
      if (deptFilter)      params.set('dept',   deptFilter);
      if (statusFilter)    params.set('status', statusFilter);
      const res = await api.get<UserPage>(`/admin/users?${params}`);
      setData(res.data);
      setSelectedIds(new Set());
    } finally { setLoading(false); }
  }, [debouncedSearch, roleFilter, deptFilter, statusFilter, page]);

  useEffect(() => { loadUsers(); }, [loadUsers]);


  const saveRole = async (userId: string) => {
    const newRole = pendingRoles[userId];
    if (!newRole) return;
    setSavingId(userId);
    setSaveErrors(prev => { const n = { ...prev }; delete n[userId]; return n; });
    try {
      await api.put(`/admin/users/${userId}/role`, { role: newRole });
      toast('Role updated', 'success');
      setPendingRoles(prev => { const n = { ...prev }; delete n[userId]; return n; });
      setData(prev => prev ? {
        ...prev,
        users: prev.users.map(u => u.id === userId ? { ...u, role: newRole } : u),
      } : prev);
    } catch (e: any) {
      setSaveErrors(prev => ({ ...prev, [userId]: e?.response?.data?.message ?? 'Error saving' }));
    } finally { setSavingId(null); }
  };

  const applyBulkRole = async () => {
    if (!bulkRole || selectedIds.size === 0) return;
    setApplyingBulk(true);
    try {
      await api.put('/admin/users/bulk-role', { userIds: [...selectedIds], role: bulkRole });
      toast(`Updated ${selectedIds.size} user(s) to ${ROLE_LABELS[bulkRole]}`, 'success');
      setSelectedIds(new Set());
      setBulkRole('');
      loadUsers();
    } catch (e: any) {
      toast(e?.response?.data?.message ?? 'Bulk update failed', 'error');
    } finally { setApplyingBulk(false); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ page: '1', limit: '9999' });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (roleFilter)      params.set('role',   roleFilter);
      if (deptFilter)      params.set('dept',   deptFilter);
      if (statusFilter)    params.set('status', statusFilter);
      const res = await api.get<UserPage>(`/admin/users?${params}`);
      exportUsersCsv(res.data.users);
    } finally { setExporting(false); }
  };

  const handleToggle = async (u: AdminUser) => {
    setTogglingId(u.id);
    try {
      const updated = await api.put<AdminUser>(`/admin/users/${u.id}/toggle`);
      toast(`${updated.data.name} ${updated.data.isActive ? 'activated' : 'deactivated'}`, 'success');
      setData(prev => prev ? {
        ...prev,
        users: prev.users.map(x => x.id === u.id ? updated.data : x),
      } : prev);
    } catch (e: any) {
      toast(e?.response?.data?.message ?? 'Failed to toggle user', 'error');
    } finally { setTogglingId(null); }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      await api.post('/admin/users', createForm);
      toast(`User "${createForm.name}" created`, 'success');
      setShowCreate(false);
      setCreateForm({ name: '', username: '', email: '', password: '', role: 'employee', department: '', jobTitle: '' });
      loadUsers();
    } catch (e: any) {
      toast(e?.response?.data?.message ?? 'Failed to create user', 'error');
    } finally { setCreating(false); }
  };

  const openEdit = (u: AdminUser) => {
    setEditUser(u);
    setEditForm({ name: u.name, department: u.department ?? '', jobTitle: u.jobTitle ?? '', managerId: u.managerId ?? '' });
    setNewPassword('');
    loadLineManagers(); // refresh so a newly-created line manager is selectable
  };

  const handleResetPassword = async () => {
    if (!editUser) return;
    if (newPassword.length < 8) { toast('Password must be at least 8 characters', 'error'); return; }
    setResettingPw(true);
    try {
      await api.put(`/admin/users/${editUser.id}/password`, { password: newPassword });
      toast('Password set — the user can now sign in with it', 'success');
      setNewPassword('');
    } catch (e: any) {
      toast(e?.response?.data?.message ?? 'Failed to set password', 'error');
    } finally { setResettingPw(false); }
  };

  const handleEdit = async () => {
    if (!editUser) return;
    setEditing(true);
    try {
      const payload = {
        ...editForm,
        managerId: editForm.managerId || null,
      };
      const updated = await api.put<{ user: AdminUser }>(`/admin/users/${editUser.id}`, payload);
      toast('User updated', 'success');
      setEditUser(null);
      setData(prev => prev ? {
        ...prev,
        users: prev.users.map(u => u.id === updated.data.user.id ? updated.data.user : u),
      } : prev);
    } catch (e: any) {
      toast(e?.response?.data?.message ?? 'Failed to update user', 'error');
    } finally { setEditing(false); }
  };

  const openRoles = (u: AdminUser) => {
    const existingRoles = [...new Set([u.role, ...(u.userRoles?.map(r => r.role) ?? [])])];
    setPendingRoleSet(new Set(existingRoles));
    setRolesUser(u);
  };

  const handleSaveRoles = async () => {
    if (!rolesUser) return;
    const roles = [...pendingRoleSet];
    if (roles.length === 0) { toast('At least one role is required', 'error'); return; }
    setSavingRoles(true);
    try {
      const updated = await api.put<{ user: AdminUser; warning?: string }>(`/admin/users/${rolesUser.id}/roles`, { roles });
      toast(updated.data.warning ?? 'Roles updated', updated.data.warning ? 'error' : 'success');
      setRolesUser(null);
      setData(prev => prev ? {
        ...prev,
        users: prev.users.map(u => u.id === updated.data.user.id ? updated.data.user : u),
      } : prev);
    } catch (e: any) {
      toast(e?.response?.data?.message ?? 'Failed to update roles', 'error');
    } finally { setSavingRoles(false); }
  };

  const users      = data?.users ?? [];
  const total      = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const allChecked = users.length > 0 && users.every(u => selectedIds.has(u.id));

  const toggleAll = () => {
    if (allChecked) {
      setSelectedIds(prev => { const n = new Set(prev); users.forEach(u => n.delete(u.id)); return n; });
    } else {
      setSelectedIds(prev => { const n = new Set(prev); users.forEach(u => n.add(u.id)); return n; });
    }
  };

  const toggleOne = (id: string) =>
    setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div className="space-y-3">

      {/* Create User Modal */}
      {showCreate && (
        <Modal title="Create User" onClose={() => setShowCreate(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Full Name" required>
                <input className={inp} value={createForm.name}
                  onChange={e => setCreateForm(p => ({ ...p, name: e.target.value }))} />
              </Field>
              <Field label="Username" required>
                <input className={inp} value={createForm.username}
                  onChange={e => setCreateForm(p => ({ ...p, username: e.target.value }))} />
              </Field>
            </div>
            <Field label="Email" required>
              <input type="email" className={inp} value={createForm.email}
                onChange={e => setCreateForm(p => ({ ...p, email: e.target.value }))} />
            </Field>
            <Field label="Password (min 8 chars)" required>
              <input type="password" className={inp} value={createForm.password}
                onChange={e => setCreateForm(p => ({ ...p, password: e.target.value }))} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Role" required>
                <select className={sel} value={createForm.role}
                  onChange={e => setCreateForm(p => ({ ...p, role: e.target.value as Role }))}>
                  {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                </select>
              </Field>
              <Field label="Department">
                <select className={sel} value={createForm.department}
                  onChange={e => setCreateForm(p => ({ ...p, department: e.target.value }))}>
                  <option value="">None</option>
                  {departmentNames.map(d => <option key={d}>{d}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Job Title">
              <input className={inp} value={createForm.jobTitle}
                onChange={e => setCreateForm(p => ({ ...p, jobTitle: e.target.value }))} />
            </Field>
          </div>
          <div className="flex gap-2 pt-2">
            <Button size="sm" disabled={creating || !createForm.name || !createForm.email || !createForm.username || !createForm.password}
              onClick={handleCreate}>
              {creating ? 'Creating…' : 'Create User'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
          </div>
        </Modal>
      )}

      {/* Edit User Modal */}
      {editUser && (
        <Modal title={`Edit: ${editUser.name}`} onClose={() => setEditUser(null)}>
          <div className="space-y-3">
            <Field label="Full Name">
              <input className={inp} value={editForm.name}
                onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} />
            </Field>
            <Field label="Department">
              <select className={sel} value={editForm.department}
                onChange={e => setEditForm(p => ({ ...p, department: e.target.value }))}>
                <option value="">None</option>
                {departmentNames.map(d => <option key={d}>{d}</option>)}
              </select>
            </Field>
            <Field label="Job Title">
              <input className={inp} value={editForm.jobTitle}
                onChange={e => setEditForm(p => ({ ...p, jobTitle: e.target.value }))} />
            </Field>
            {[editUser.role, ...(editUser.userRoles?.map(r => r.role) ?? [])].some(r => r === 'employee' || r === 'it_staff') && (
              <Field label="Line Manager">
                <select className={sel} value={editForm.managerId}
                  onChange={e => setEditForm(p => ({ ...p, managerId: e.target.value }))}>
                  <option value="">None (unassigned)</option>
                  {lineManagers.map(lm => (
                    <option key={lm.id} value={lm.id}>{lm.name} {lm.department ? `— ${lm.department}` : ''}</option>
                  ))}
                </select>
              </Field>
            )}
            <p className="text-[10px] text-gray-400">
              To change role, use the role dropdown in the table. To toggle active, use the toggle button.
            </p>

            {/* Password (local accounts only — SSO users sign in with Microsoft) */}
            <div className="pt-3 mt-1 border-t border-gray-100">
              {editUser.azureId ? (
                <p className="text-[11px] text-gray-400">
                  This is a Microsoft (SSO) account — it signs in with Microsoft, so no password can be set.
                </p>
              ) : (
                <Field label="Set / Reset Password">
                  <div className="flex gap-2">
                    <input
                      type="password"
                      className={inp}
                      value={newPassword}
                      autoComplete="new-password"
                      placeholder="New password (min 8 chars)"
                      onChange={e => setNewPassword(e.target.value)}
                    />
                    <Button size="sm" variant="secondary" disabled={resettingPw || newPassword.length < 8}
                      onClick={handleResetPassword}>
                      {resettingPw ? 'Setting…' : 'Set'}
                    </Button>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1">Lets this user sign in with their username/email and password.</p>
                </Field>
              )}
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button size="sm" disabled={editing} onClick={handleEdit}>
              {editing ? 'Saving…' : 'Save Changes'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditUser(null)}>Cancel</Button>
          </div>
        </Modal>
      )}

      {/* Manage Roles Modal */}
      {rolesUser && (
        <Modal title={`Manage Roles: ${rolesUser.name}`} onClose={() => setRolesUser(null)}>
          <p className="text-xs text-gray-400 -mt-2">Select all roles for this user. The highest role becomes their primary role.</p>
          {isProtectedAdminEmail(rolesUser.email) && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              This account is protected — Admin and IT Manager can't be removed. Every other role stays free to assign or remove.
            </p>
          )}
          <div className="space-y-2">
            {ROLES.map(r => {
              const locked = isProtectedAdminEmail(rolesUser.email) && (r === 'admin' || r === 'it_manager');
              return (
              <label key={r} className={`flex items-center gap-3 p-2.5 rounded-lg border border-gray-200 transition-colors ${
                locked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:border-violet-300'}`}>
                <input
                  type="checkbox"
                  checked={pendingRoleSet.has(r)}
                  disabled={locked}
                  onChange={() => {
                    if (locked) return;
                    setPendingRoleSet(prev => {
                      const n = new Set(prev);
                      n.has(r) ? n.delete(r) : n.add(r);
                      return n;
                    });
                  }}
                  className="rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                />
                <span className="text-sm font-medium text-[#010E23]">{ROLE_LABELS[r]}</span>
              </label>
              );
            })}
          </div>
          <div className="flex gap-2 pt-2">
            <Button size="sm" disabled={savingRoles || pendingRoleSet.size === 0} onClick={handleSaveRoles}>
              {savingRoles ? 'Saving…' : 'Save Roles'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setRolesUser(null)}>Cancel</Button>
          </div>
        </Modal>
      )}

      {/* Filters + actions toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"/>
          </svg>
          <input type="text" placeholder="Search name, email, dept…" value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-violet-500" />
        </div>

        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 outline-none focus:border-violet-500 bg-white">
          <option value="">All Roles</option>
          {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
        </select>

        <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 outline-none focus:border-violet-500 bg-white">
          <option value="">All Departments</option>
          {departmentNames.map(d => <option key={d}>{d}</option>)}
        </select>

        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 outline-none focus:border-violet-500 bg-white">
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>

        <span className="flex-1" />

        {/* Create User */}
        <button onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-violet-600 text-white hover:bg-violet-700 transition-colors">
          + Create User
        </button>

        {/* Export CSV */}
        <button onClick={handleExport} disabled={exporting}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50">
          <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd"/>
          </svg>
          {exporting ? 'Exporting…' : 'CSV'}
        </button>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 bg-violet-50 border border-violet-100 rounded-lg px-4 py-2">
          <span className="text-sm font-semibold text-violet-800">{selectedIds.size} selected</span>
          <span className="text-violet-200 select-none">|</span>
          <span className="text-sm text-violet-700">Assign role:</span>
          <select value={bulkRole} onChange={e => setBulkRole(e.target.value as Role | '')}
            className="text-sm border border-violet-200 rounded-lg px-2.5 py-1 outline-none focus:border-violet-500 bg-white">
            <option value="">Select…</option>
            {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </select>
          <button onClick={applyBulkRole} disabled={!bulkRole || applyingBulk}
            className="px-3 py-1 rounded-lg text-sm font-medium bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-40">
            {applyingBulk ? 'Applying…' : 'Apply'}
          </button>
          <button onClick={() => setSelectedIds(new Set())}
            className="ml-auto text-xs text-violet-400 hover:text-violet-700">
            Clear
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ minWidth: '960px' }}>
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-3 py-3 w-10">
                  <input type="checkbox" checked={allChecked && users.length > 0} onChange={toggleAll}
                    className="rounded border-gray-300 text-violet-600 focus:ring-violet-500" />
                </th>
                {['User', 'Department', 'Line Manager', 'M365 Status', 'Portal Role', 'Last Login', ''].map(h => (
                  <th key={h} className="text-left text-xs font-semibold text-gray-400 px-3 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading
                ? [...Array(6)].map((_, i) => (
                    <tr key={i} className="border-b border-gray-50 animate-pulse">
                      <td className="px-3 py-3.5"><div className="w-4 h-4 bg-gray-100 rounded" /></td>
                      <td className="px-3 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-gray-100" />
                          <div className="space-y-1.5">
                            <div className="w-28 h-2.5 bg-gray-100 rounded" />
                            <div className="w-40 h-2 bg-gray-50 rounded" />
                          </div>
                        </div>
                      </td>
                      {[...Array(6)].map((_, j) => (
                        <td key={j} className="px-3 py-3.5"><div className="w-16 h-2.5 bg-gray-100 rounded" /></td>
                      ))}
                    </tr>
                  ))
                : users.length === 0
                  ? <tr><td colSpan={8} className="text-center text-sm text-gray-400 py-14">No users match the current filters</td></tr>
                  : users.map(u => {
                      const isSelf      = u.id === currentUserId;
                      const isProtected = isProtectedAdminEmail(u.email);
                      const hasPending  = pendingRoles[u.id] !== undefined;
                      const activeRole  = pendingRoles[u.id] ?? u.role;

                      return (
                        <tr key={u.id}
                          className={`border-b border-gray-50 transition-colors hover:bg-gray-50/60 ${isSelf && !isProtected ? 'opacity-60' : ''}`}>
                          <td className="px-3 py-3">
                            <input type="checkbox" checked={selectedIds.has(u.id)} onChange={() => toggleOne(u.id)}
                              className="rounded border-gray-300 text-violet-600 focus:ring-violet-500" />
                          </td>

                          {/* Avatar + Name + Email */}
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-2.5">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${avatarColor(u.role)}`}>
                                {initials(u.name)}
                              </div>
                              <div className="min-w-0">
                                <p className="text-xs font-semibold text-[#010E23] truncate max-w-[160px]">{u.name}</p>
                                <p className="text-[10px] text-gray-400 truncate max-w-[160px]">{u.email}</p>
                                {u.jobTitle && <p className="text-[10px] text-gray-300 truncate max-w-[160px]">{u.jobTitle}</p>}
                              </div>
                            </div>
                          </td>

                          <td className="px-3 py-3 text-xs text-gray-500">{u.department ?? <span className="text-gray-300">—</span>}</td>

                          {/* Line Manager */}
                          <td className="px-3 py-3 text-xs text-gray-500">
                            {[u.role, ...(u.userRoles?.map(r => r.role) ?? [])].some(r => r === 'employee' || r === 'it_staff')
                              ? u.managerId
                                ? <span className="text-[#010E23]">
                                    {lineManagers.find(lm => lm.id === u.managerId)?.name ?? <span className="text-gray-300">Assigned</span>}
                                  </span>
                                : <span className="text-red-500 font-medium">Not assigned</span>
                              : <span className="text-gray-300">—</span>
                            }
                          </td>

                          {/* M365 Status */}
                          <td className="px-3 py-3">
                            {!u.azureId
                              ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-500">Manual</span>
                              : u.isActive
                                ? <span title="Managed in Azure AD" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-700 cursor-help">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />Active
                                  </span>
                                : <span title="Disabled in Azure AD" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-50 text-red-600 cursor-help">
                                    <span className="w-1.5 h-1.5 rounded-full bg-red-400" />Disabled
                                  </span>
                            }
                          </td>

                          {/* Role column */}
                          <td className="px-3 py-3">
                            <div className="space-y-1">
                              {isSelf || isProtected
                                ? <span title={isSelf ? 'Cannot change own role' : 'Protected account — primary role is locked to Admin'}
                                    className="inline-block px-2 py-0.5 rounded-md text-[10px] font-semibold bg-[#010E23] text-white cursor-not-allowed opacity-70">
                                    {ROLE_LABELS[u.role]}
                                  </span>
                                : <>
                                    <select value={activeRole}
                                      onChange={e => {
                                        const nr = e.target.value as Role;
                                        if (nr === u.role) {
                                          setPendingRoles(prev => { const n = { ...prev }; delete n[u.id]; return n; });
                                        } else {
                                          setPendingRoles(prev => ({ ...prev, [u.id]: nr }));
                                        }
                                        setSaveErrors(prev => { const n = { ...prev }; delete n[u.id]; return n; });
                                      }}
                                      className={`text-xs rounded-lg px-2 py-1 outline-none focus:ring-1 focus:ring-violet-200 ${
                                        hasPending
                                          ? 'border border-violet-400 bg-violet-50 text-violet-700'
                                          : 'border border-gray-200 bg-white text-gray-700'
                                      }`}>
                                      {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                                    </select>
                                    {saveErrors[u.id] && (
                                      <p className="text-[10px] text-red-500 mt-0.5 max-w-[130px]">{saveErrors[u.id]}</p>
                                    )}
                                  </>
                              }
                              {u.userRoles && u.userRoles.filter(r => r.role !== u.role).length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {u.userRoles.filter(r => r.role !== u.role).map(r => (
                                    <span key={r.role} className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-violet-50 text-violet-700 border border-violet-100">
                                      +{ROLE_LABELS[r.role]}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {u.role === 'admin' && noITManager && (
                                <span title="No IT Manager assigned — this admin acts as the IT Manager approver" className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-amber-50 text-amber-700 border border-amber-100 cursor-help">
                                  IT Manager (default)
                                </span>
                              )}
                            </div>
                          </td>

                          <td className="px-3 py-3 text-[10px] text-gray-400 whitespace-nowrap">
                            {u.lastLogin ? fmtDT(u.lastLogin) : <span className="text-gray-300">Never</span>}
                          </td>

                          {/* Actions */}
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-1.5">
                              {!isSelf && hasPending && (
                                <button onClick={() => saveRole(u.id)} disabled={savingId === u.id}
                                  className="px-2 py-0.5 text-[10px] font-semibold rounded-md bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 whitespace-nowrap">
                                  {savingId === u.id ? '…' : 'Save'}
                                </button>
                              )}
                              {(!isSelf || isProtected) && (
                                <button onClick={() => openEdit(u)}
                                  className="px-2 py-0.5 text-[10px] font-medium rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 whitespace-nowrap">
                                  Edit
                                </button>
                              )}
                              {(!isSelf || isProtected) && (
                                <button onClick={() => openRoles(u)}
                                  className="px-2 py-0.5 text-[10px] font-medium rounded-md border border-violet-200 text-violet-600 hover:bg-violet-50 whitespace-nowrap">
                                  Roles
                                </button>
                              )}
                              {!isSelf && !isProtected && !u.azureId && (
                                <button onClick={() => handleToggle(u)} disabled={togglingId === u.id}
                                  className={`px-2 py-0.5 text-[10px] font-medium rounded-md whitespace-nowrap ${
                                    u.isActive
                                      ? 'border border-red-200 text-red-600 hover:bg-red-50'
                                      : 'border border-emerald-200 text-emerald-700 hover:bg-emerald-50'
                                  }`}>
                                  {togglingId === u.id ? '…' : u.isActive ? 'Deactivate' : 'Activate'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
              }
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/50">
          <span className="text-xs text-gray-400">
            {total === 0
              ? 'No users'
              : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total}`}
          </span>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
              className="px-2.5 py-1 text-xs rounded-lg border border-gray-200 text-gray-600 hover:bg-white disabled:opacity-35 disabled:cursor-not-allowed">
              ← Prev
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const start = Math.max(1, Math.min(page - 2, totalPages - 4));
              const n = start + i;
              if (n > totalPages) return null;
              return (
                <button key={n} onClick={() => setPage(n)}
                  className={`w-7 h-7 text-xs rounded-lg transition-colors ${
                    n === page ? 'bg-[#010E23] text-white font-semibold' : 'border border-gray-200 text-gray-600 hover:bg-white'
                  }`}>
                  {n}
                </button>
              );
            })}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              className="px-2.5 py-1 text-xs rounded-lg border border-gray-200 text-gray-600 hover:bg-white disabled:opacity-35 disabled:cursor-not-allowed">
              Next →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── SettingsPanel helpers ──────────────────────────────────────────────────────

function SettingRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-4 py-1.5">
      <span className="text-xs font-medium text-gray-600 w-40 flex-shrink-0 pt-1.5">{label}</span>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function Toggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${value ? 'bg-violet-600' : 'bg-gray-200'}`}
    >
      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform ${value ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
    </button>
  );
}

// ── SettingsPanel ─────────────────────────────────────────────────────────────

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

function SettingsPanel({ settings, onBatchSave }: {
  settings: Setting[];
  onBatchSave: (edits: Record<string, string>) => Promise<void>;
}) {
  const { toast }                         = useToast();
  const [edits,          setEdits]        = useState<Record<string, string>>({});
  const [saving,         setSaving]       = useState<string | null>(null);
  const [resetConfirm,   setResetConfirm] = useState(false);
  const [resetting,      setResetting]    = useState(false);

  const val = (key: string, fallback = '') =>
    edits[key] ?? settings.find(s => s.key === key)?.value ?? fallback;

  const set = (key: string, value: string) =>
    setEdits(prev => ({ ...prev, [key]: value }));

  const sectionDirty = (keys: string[]) =>
    keys.some(k => edits[k] !== undefined && edits[k] !== (settings.find(s => s.key === k)?.value ?? ''));

  const saveSection = async (sectionId: string, keys: string[]) => {
    const dirty: Record<string, string> = {};
    for (const k of keys) {
      const current = settings.find(s => s.key === k)?.value ?? '';
      if (edits[k] !== undefined && edits[k] !== current) dirty[k] = edits[k];
    }
    if (!Object.keys(dirty).length) return;
    setSaving(sectionId);
    try {
      await onBatchSave(dirty);
      setEdits(prev => { const n = { ...prev }; keys.forEach(k => delete n[k]); return n; });
    } finally { setSaving(null); }
  };

  function SectionCard({ title, sectionId, keys, children }: {
    title: string; sectionId: string; keys: string[]; children: React.ReactNode;
  }) {
    const dirty = sectionDirty(keys);
    return (
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-3">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-[#010E23]">{title}</h3>
          <button
            onClick={() => saveSection(sectionId, keys)}
            disabled={!dirty || saving === sectionId}
            className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
              dirty
                ? 'bg-violet-600 text-white hover:bg-violet-700'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {saving === sectionId ? 'Saving…' : `Save ${title}`}
          </button>
        </div>
        <div className="divide-y divide-gray-50">{children}</div>
      </div>
    );
  }

  // Reference number live preview
  const refPreview = () => {
    const pfx  = (val('ref_company_prefix', 'PSH') || 'PSH').toUpperCase();
    const dept = (val('ref_dept_code',      'IT')  || 'IT').toUpperCase();
    const form = (val('ref_form_code',      'SRF') || 'SRF').toUpperCase();
    const seq  = String(parseInt(val('ref_seq_current', '1'), 10) || 1).padStart(5, '0');
    return `${pfx}-${dept}-${form}-${seq}-Name-ServiceItem`;
  };

  const doResetSequence = async () => {
    setResetting(true);
    try {
      await api.post('/admin/form-registry/reset-seq', { formCode: 'SRF' });
      toast('Sequence reset to 00001. Button is now permanently disabled.', 'success');
      setResetConfirm(false);
    } catch (e: any) {
      toast(e?.response?.data?.message ?? 'Reset failed', 'error');
    } finally {
      setResetting(false);
    }
  };

  // Working days helpers
  const workDaysArr = val('work_days', 'Mon,Tue,Wed,Thu,Fri').split(',').map(d => d.trim()).filter(Boolean);
  const toggleWorkDay = (day: string) => {
    const s = new Set(workDaysArr);
    s.has(day) ? s.delete(day) : s.add(day);
    set('work_days', DAYS.filter(d => s.has(d)).join(','));
  };

  if (settings.length === 0) {
    return <p className="text-xs text-gray-400 text-center py-8">No settings found. Run seed or rebuild.</p>;
  }

  return (
    <div className="space-y-5 max-w-2xl">

      {/* ── Section 1: Company Information ── */}
      <SectionCard title="Company Information" sectionId="company"
        keys={['company_name', 'portal_title', 'support_email', 'portal_url']}>
        <SettingRow label="Company Name">
          <input className={inp} value={val('company_name', 'Permus Software House')}
            onChange={e => set('company_name', e.target.value)} />
        </SettingRow>
        <SettingRow label="Portal Title">
          <input className={inp} value={val('portal_title', 'ServiceDesk')}
            onChange={e => set('portal_title', e.target.value)} />
        </SettingRow>
        <SettingRow label="Support Email">
          <input type="email" className={inp} value={val('support_email', 'cloudsecurity@permus.io')}
            onChange={e => set('support_email', e.target.value)} />
        </SettingRow>
        <SettingRow label="Portal URL">
          <input className={inp} value={val('portal_url', 'http://localhost:3000')}
            onChange={e => set('portal_url', e.target.value)} />
        </SettingRow>
      </SectionCard>

      {/* ── Section 2: Reference Number Format ── */}
      <SectionCard title="Reference Number Format" sectionId="refnum"
        keys={['ref_company_prefix', 'ref_dept_code', 'ref_form_code']}>
        <SettingRow label="Company Prefix">
          <input className={inp + ' w-36'} value={val('ref_company_prefix', 'PSH')}
            onChange={e => set('ref_company_prefix', e.target.value.toUpperCase())} />
        </SettingRow>
        <SettingRow label="Department Code">
          <input className={inp + ' w-36'} value={val('ref_dept_code', 'IT')}
            onChange={e => set('ref_dept_code', e.target.value.toUpperCase())} />
        </SettingRow>
        <SettingRow label="Form Code">
          <input className={inp + ' w-36'} value={val('ref_form_code', 'SRF')}
            onChange={e => set('ref_form_code', e.target.value.toUpperCase())} />
        </SettingRow>
        <SettingRow label="Current Sequence">
          <div>
            <input className={inp + ' w-36 opacity-60 cursor-not-allowed'} readOnly
              value={String(parseInt(val('ref_seq_current', '1'), 10) || 1).padStart(5, '0')} />
            <p className="text-[10px] text-gray-400 mt-0.5">Managed via Form Registry tab</p>
          </div>
        </SettingRow>
        <div className="mt-2 rounded-lg bg-[#F4F4F8] border border-gray-200 px-4 py-3">
          <p className="text-[10px] font-semibold text-gray-500 mb-1 uppercase tracking-wide">Live Preview</p>
          <code className="text-xs font-mono text-[#26148C]">{refPreview()}</code>
        </div>
        {/* One-time production reset */}
        {(() => {
          const alreadyDone = val('ref_reset_done', 'false') === 'true';
          return (
            <div className="mt-3 rounded-lg border border-red-100 bg-red-50 px-4 py-3 flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-semibold text-red-700">One-Time Production Reset</p>
                <p className="text-[10px] text-red-500 mt-0.5">
                  {alreadyDone
                    ? 'Reset already performed — this action is permanently disabled.'
                    : 'Reset the sequence counter to 00001 for the production launch. Irreversible.'}
                </p>
              </div>
              <button
                disabled={alreadyDone || resetting}
                onClick={() => setResetConfirm(true)}
                className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  alreadyDone
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                {alreadyDone ? 'Done' : 'Reset to #00001'}
              </button>
            </div>
          );
        })()}
      </SectionCard>

      {/* Confirmation modal for sequence reset */}
      {resetConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4">
            <h2 className="text-base font-bold text-red-700">Reset Reference Sequence?</h2>
            <p className="text-sm text-gray-600">
              This will reset the sequence counter to <strong>00001</strong> for the next ticket.
              Existing tickets keep their current reference numbers. This action is <strong>permanent and irreversible</strong>.
            </p>
            <div className="flex gap-3 pt-1">
              <button
                disabled={resetting}
                onClick={doResetSequence}
                className="flex-1 py-2 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-60 transition-colors"
              >
                {resetting ? 'Resetting…' : 'Yes, Reset to #00001'}
              </button>
              <button
                disabled={resetting}
                onClick={() => setResetConfirm(false)}
                className="flex-1 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Section 3: SLA Configuration ── */}
      <SectionCard title="SLA Configuration" sectionId="sla"
        keys={['sla_critical', 'sla_high', 'sla_medium', 'sla_low']}>
        {([
          { key: 'sla_critical', label: 'Critical Priority', def: '4'  },
          { key: 'sla_high',     label: 'High Priority',     def: '8'  },
          { key: 'sla_medium',   label: 'Medium Priority',   def: '24' },
          { key: 'sla_low',      label: 'Low Priority',      def: '72' },
        ] as const).map(({ key, label, def }) => (
          <SettingRow key={key} label={label}>
            <div className="flex items-center gap-2">
              <input type="number" min={1} className={inp + ' w-24'} value={val(key, def)}
                onChange={e => set(key, e.target.value)} />
              <span className="text-xs text-gray-500">hours</span>
            </div>
          </SettingRow>
        ))}
      </SectionCard>

      {/* ── Section 4: Email Configuration ── */}
      <SectionCard title="Email Configuration" sectionId="email"
        keys={['email_from', 'email_signature', 'notify_on_submit', 'notify_on_approve', 'notify_on_reject', 'notify_on_complete']}>
        <SettingRow label="Email From">
          <input type="email" className={inp} value={val('email_from', 'cloudsecurity@permus.io')}
            onChange={e => set('email_from', e.target.value)} />
        </SettingRow>
        <SettingRow label="Email Signature">
          <input className={inp} value={val('email_signature', 'Permus IT Team')}
            onChange={e => set('email_signature', e.target.value)} />
        </SettingRow>
        {([
          { key: 'notify_on_submit',   label: 'Notify on Submit'   },
          { key: 'notify_on_approve',  label: 'Notify on Approve'  },
          { key: 'notify_on_reject',   label: 'Notify on Reject'   },
          { key: 'notify_on_complete', label: 'Notify on Complete' },
        ] as const).map(({ key, label }) => (
          <SettingRow key={key} label={label}>
            <Toggle value={val(key, 'true') === 'true'} onChange={v => set(key, String(v))} />
          </SettingRow>
        ))}
      </SectionCard>

      {/* ── Section 5: Working Hours ── */}
      <SectionCard title="Working Hours" sectionId="hours"
        keys={['work_start', 'work_end', 'work_days', 'timezone']}>
        <SettingRow label="Work Start Time">
          <input type="time" className={inp + ' w-36'} value={val('work_start', '08:00')}
            onChange={e => set('work_start', e.target.value)} />
        </SettingRow>
        <SettingRow label="Work End Time">
          <input type="time" className={inp + ' w-36'} value={val('work_end', '17:00')}
            onChange={e => set('work_end', e.target.value)} />
        </SettingRow>
        <SettingRow label="Working Days">
          <div className="flex flex-wrap gap-3 pt-0.5">
            {DAYS.map(day => (
              <label key={day} className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={workDaysArr.includes(day)} onChange={() => toggleWorkDay(day)}
                  className="rounded border-gray-300 text-violet-600 focus:ring-violet-500" />
                <span className="text-xs text-gray-700">{day}</span>
              </label>
            ))}
          </div>
        </SettingRow>
        <SettingRow label="Timezone">
          <select className={sel} value={val('timezone', 'Asia/Riyadh')} onChange={e => set('timezone', e.target.value)}>
            {[
              'Asia/Riyadh', 'Asia/Dubai', 'UTC', 'Europe/London', 'Europe/Paris',
              'America/New_York', 'America/Chicago', 'America/Los_Angeles',
              'Asia/Kolkata', 'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney',
            ].map(tz => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </SettingRow>
      </SectionCard>

      {/* ── Section 6: System Settings ── */}
      <SectionCard title="System Settings" sectionId="system"
        keys={['session_timeout', 'max_upload_mb', 'default_role', 'auto_approve_admin']}>
        <SettingRow label="Session Timeout">
          <div className="flex items-center gap-2">
            <input type="number" min={1} className={inp + ' w-24'} value={val('session_timeout', '8')}
              onChange={e => set('session_timeout', e.target.value)} />
            <span className="text-xs text-gray-500">hours</span>
          </div>
        </SettingRow>
        <SettingRow label="Max File Upload">
          <div className="flex items-center gap-2">
            <input type="number" min={1} className={inp + ' w-24'} value={val('max_upload_mb', '10')}
              onChange={e => set('max_upload_mb', e.target.value)} />
            <span className="text-xs text-gray-500">MB</span>
          </div>
        </SettingRow>
        <SettingRow label="Default Role">
          <select className={sel + ' w-48'} value={val('default_role', 'employee')} onChange={e => set('default_role', e.target.value)}>
            {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </select>
        </SettingRow>
        <SettingRow label="Auto-approve Admin">
          <Toggle value={val('auto_approve_admin', 'false') === 'true'} onChange={v => set('auto_approve_admin', String(v))} />
        </SettingRow>
      </SectionCard>

    </div>
  );
}

// ── DepartmentsPanel ──────────────────────────────────────────────────────────

function DepartmentsPanel() {
  const { toast } = useToast();
  const [depts,   setDepts]   = useState<Dept[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', code: '' });
  const [saving,  setSaving]  = useState(false);
  const [editId,  setEditId]  = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get<Dept[]>('/admin/departments');
      setDepts(r.data);
    } catch { /* ignore if endpoint not yet seeded */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const addDept = async () => {
    if (!addForm.name || !addForm.code) return;
    setSaving(true);
    try {
      const r = await api.post<Dept>('/admin/departments', addForm);
      toast(`Department "${r.data.name}" created`, 'success');
      setDepts(prev => [...prev, r.data].sort((a, b) => a.name.localeCompare(b.name)));
      setAddForm({ name: '', code: '' });
      setShowAdd(false);
      invalidateDepartments(); // refresh live dropdowns app-wide (no reload)
    } catch (e: any) {
      toast(e?.response?.data?.message ?? 'Failed to create department', 'error');
    } finally { setSaving(false); }
  };

  const saveEdit = async (id: string) => {
    setEditSaving(true);
    try {
      const r = await api.put<Dept>(`/admin/departments/${id}`, { name: editName });
      toast('Department updated', 'success');
      setDepts(prev => prev.map(d => d.id === id ? { ...r.data, userCount: d.userCount } : d));
      setEditId(null);
      invalidateDepartments(); // refresh live dropdowns app-wide (no reload)
    } catch (e: any) {
      toast(e?.response?.data?.message ?? 'Failed to update', 'error');
    } finally { setEditSaving(false); }
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-[#010E23]">Departments ({depts.length})</h2>
        <Button size="sm" onClick={() => setShowAdd(v => !v)}>+ Add Department</Button>
      </div>

      {showAdd && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Department Name" required>
              <input className={inp} placeholder="e.g. Finance" value={addForm.name}
                onChange={e => setAddForm(p => ({ ...p, name: e.target.value }))} />
            </Field>
            <Field label="Code (short, unique)" required>
              <input className={inp} placeholder="e.g. FIN" value={addForm.code}
                onChange={e => setAddForm(p => ({ ...p, code: e.target.value.toUpperCase() }))} />
            </Field>
          </div>
          <div className="flex gap-2">
            <Button size="sm" disabled={saving || !addForm.name || !addForm.code} onClick={addDept}>
              {saving ? 'Saving…' : 'Create'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)}>Cancel</Button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {loading
          ? <div className="py-8 text-center text-xs text-gray-400">Loading…</div>
          : depts.length === 0
            ? <div className="py-8 text-center text-xs text-gray-400">No departments seeded yet. Add one above.</div>
            : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    {['Name', 'Code', 'Users', ''].map(h => (
                      <th key={h} className="text-left text-xs font-semibold text-gray-400 px-4 py-2.5">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {depts.map(d => (
                    <tr key={d.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="px-4 py-3">
                        {editId === d.id
                          ? <input className={inp + ' py-1'} value={editName}
                              onChange={e => setEditName(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') saveEdit(d.id); if (e.key === 'Escape') setEditId(null); }}
                              autoFocus />
                          : <span className="text-xs font-medium text-[#010E23]">{d.name}</span>
                        }
                      </td>
                      <td className="px-4 py-3 font-mono text-[10px] text-violet-700">{d.code}</td>
                      <td className="px-4 py-3 text-xs text-gray-500">{d.userCount}</td>
                      <td className="px-4 py-3">
                        {editId === d.id
                          ? <div className="flex gap-1.5">
                              <button onClick={() => saveEdit(d.id)} disabled={editSaving}
                                className="text-[10px] text-violet-600 hover:underline">{editSaving ? '…' : 'Save'}</button>
                              <button onClick={() => setEditId(null)}
                                className="text-[10px] text-gray-400 hover:underline">Cancel</button>
                            </div>
                          : <button onClick={() => { setEditId(d.id); setEditName(d.name); }}
                              className="text-[10px] text-gray-400 hover:text-violet-600">Edit</button>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
        }
      </div>
    </div>
  );
}

// ── WorkStatusesPanel ─────────────────────────────────────────────────────────

interface WorkStatusRow { id: string; label: string; color: string; isActive: boolean; sortOrder: number; }

function WorkStatusesPanel() {
  const { toast } = useToast();
  const [rows,    setRows]    = useState<WorkStatusRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [addForm, setAddForm] = useState({ label: '', color: '#2563EB' });
  const [saving,  setSaving]  = useState(false);
  const [editId,  setEditId]  = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ label: '', color: '' });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get<WorkStatusRow[]>('/admin/work-statuses');
      setRows(r.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const afterChange = async () => { await load(); invalidateWorkStatuses(); };

  const addStatus = async () => {
    if (!addForm.label.trim()) return;
    setSaving(true);
    try {
      await api.post('/admin/work-statuses', { ...addForm, sortOrder: rows.length + 1 });
      toast(`Work status "${addForm.label}" created`, 'success');
      setAddForm({ label: '', color: '#2563EB' });
      await afterChange();
    } catch (e: any) {
      toast(e?.response?.data?.message ?? 'Failed to create work status', 'error');
    } finally { setSaving(false); }
  };

  const saveEdit = async (id: string) => {
    try {
      await api.put(`/admin/work-statuses/${id}`, { label: editForm.label, color: editForm.color });
      toast('Work status updated', 'success');
      setEditId(null);
      await afterChange();
    } catch (e: any) {
      toast(e?.response?.data?.message ?? 'Failed to update', 'error');
    }
  };

  const toggleActive = async (row: WorkStatusRow) => {
    try {
      await api.put(`/admin/work-statuses/${row.id}`, { isActive: !row.isActive });
      await afterChange();
    } catch (e: any) {
      toast(e?.response?.data?.message ?? 'Failed to toggle', 'error');
    }
  };

  const remove = async (row: WorkStatusRow) => {
    if (!window.confirm(`Delete work status "${row.label}"? Tickets keep their existing value.`)) return;
    try {
      await api.delete(`/admin/work-statuses/${row.id}`);
      toast('Work status deleted', 'success');
      await afterChange();
    } catch (e: any) {
      toast(e?.response?.data?.message ?? 'Failed to delete', 'error');
    }
  };

  const move = async (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= rows.length) return;
    const a = rows[index], b = rows[target];
    try {
      await Promise.all([
        api.put(`/admin/work-statuses/${a.id}`, { sortOrder: b.sortOrder }),
        api.put(`/admin/work-statuses/${b.id}`, { sortOrder: a.sortOrder }),
      ]);
      await afterChange();
    } catch { toast('Failed to reorder', 'error'); }
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <div>
        <h2 className="text-sm font-semibold text-[#010E23]">Ticket Work Statuses ({rows.length})</h2>
        <p className="text-xs text-gray-400 mt-0.5">
          Informational sub-statuses IT staff can set on a ticket, separate from the approval status.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-wrap items-end gap-3">
        <Field label="Label" required>
          <input className={inp} placeholder="e.g. Waiting for Parts" value={addForm.label}
            onChange={e => setAddForm(p => ({ ...p, label: e.target.value }))} />
        </Field>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Color</label>
          <input type="color" className="h-9 w-12 rounded border border-gray-200 bg-white p-0.5"
            value={addForm.color} onChange={e => setAddForm(p => ({ ...p, color: e.target.value }))} />
        </div>
        <Button size="sm" disabled={saving || !addForm.label.trim()} onClick={addStatus}>
          {saving ? 'Saving…' : '+ Add'}
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {loading
          ? <div className="py-8 text-center text-xs text-gray-400">Loading…</div>
          : rows.length === 0
            ? <div className="py-8 text-center text-xs text-gray-400">No work statuses yet. Add one above.</div>
            : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    {['Order', 'Status', 'Active', ''].map(h => (
                      <th key={h} className="text-left text-xs font-semibold text-gray-400 px-4 py-2.5">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex flex-col leading-none">
                          <button onClick={() => move(i, -1)} disabled={i === 0}
                            className="text-[10px] text-gray-400 hover:text-violet-600 disabled:opacity-30">▲</button>
                          <button onClick={() => move(i, 1)} disabled={i === rows.length - 1}
                            className="text-[10px] text-gray-400 hover:text-violet-600 disabled:opacity-30">▼</button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {editId === r.id ? (
                          <div className="flex items-center gap-2">
                            <input type="color" className="h-7 w-9 rounded border border-gray-200 p-0.5"
                              value={editForm.color} onChange={e => setEditForm(p => ({ ...p, color: e.target.value }))} />
                            <input className={inp + ' py-1'} value={editForm.label}
                              onChange={e => setEditForm(p => ({ ...p, label: e.target.value }))}
                              onKeyDown={e => { if (e.key === 'Enter') saveEdit(r.id); if (e.key === 'Escape') setEditId(null); }}
                              autoFocus />
                          </div>
                        ) : (
                          <span className="text-[11px] font-medium rounded-full px-2 py-0.5"
                            style={{ background: `${r.color}1A`, color: r.color }}>{r.label}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={() => toggleActive(r)}
                          className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${r.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {r.isActive ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        {editId === r.id ? (
                          <div className="flex gap-1.5">
                            <button onClick={() => saveEdit(r.id)} className="text-[10px] text-violet-600 hover:underline">Save</button>
                            <button onClick={() => setEditId(null)} className="text-[10px] text-gray-400 hover:underline">Cancel</button>
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <button onClick={() => { setEditId(r.id); setEditForm({ label: r.label, color: r.color }); }}
                              className="text-[10px] text-gray-400 hover:text-violet-600">Edit</button>
                            <button onClick={() => remove(r)}
                              className="text-[10px] text-gray-400 hover:text-red-600">Delete</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
        }
      </div>
    </div>
  );
}

// ── FormRegistryPanel ─────────────────────────────────────────────────────────

const AUDIT_PAGE_SIZE = 50;

function FormRegistryPanel() {
  const { toast } = useToast();
  const [forms,   setForms]   = useState<FormReg[]>([]);
  const [loading, setLoading] = useState(true);
  const [editCode, setEditCode] = useState<string | null>(null);
  const [seqValue, setSeqValue] = useState('');
  const [saving,   setSaving]   = useState(false);

  // Audit log state
  const [audit,        setAudit]        = useState<AuditPage | null>(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditPage,    setAuditPage]    = useState(1);
  const [actionFilter,  setActionFilter]  = useState('');
  const [resourceFilter, setResourceFilter] = useState('');
  const [fromFilter,   setFromFilter]   = useState('');
  const [toFilter,     setToFilter]     = useState('');

  const loadForms = async () => {
    setLoading(true);
    try {
      const r = await api.get<FormReg[]>('/admin/form-registry');
      setForms(r.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const loadAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const params = new URLSearchParams({ page: String(auditPage), limit: String(AUDIT_PAGE_SIZE) });
      if (actionFilter)   params.set('action',       actionFilter);
      if (resourceFilter) params.set('resourceType', resourceFilter);
      if (fromFilter)     params.set('from',         fromFilter);
      if (toFilter)       params.set('to',           toFilter);
      const r = await api.get<AuditPage>(`/admin/audit-log?${params}`);
      setAudit(r.data);
    } catch { /* ignore */ }
    finally { setAuditLoading(false); }
  }, [auditPage, actionFilter, resourceFilter, fromFilter, toFilter]);

  useEffect(() => { loadForms(); }, []);
  useEffect(() => { setAuditPage(1); }, [actionFilter, resourceFilter, fromFilter, toFilter]);
  useEffect(() => { loadAudit(); }, [loadAudit]);

  const saveSeq = async (code: string) => {
    const val = parseInt(seqValue, 10);
    if (!val || val < 1) { toast('Enter a valid sequence number (≥1)', 'error'); return; }
    setSaving(true);
    try {
      await api.put(`/admin/form-registry/${code}`, { seqCurrent: val });
      toast('Sequence updated', 'success');
      setForms(prev => prev.map(f => f.formCode === code ? { ...f, seqCurrent: val } : f));
      setEditCode(null);
    } catch (e: any) {
      toast(e?.response?.data?.message ?? 'Failed to update sequence', 'error');
    } finally { setSaving(false); }
  };

  const auditEntries = audit?.entries ?? [];
  const auditTotal   = audit?.total   ?? 0;
  const auditPages   = Math.max(1, Math.ceil(auditTotal / AUDIT_PAGE_SIZE));

  const inputSm = 'text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 outline-none focus:border-violet-600';

  return (
    <div className="space-y-6">

      {/* Reference Number Format */}
      <div className="bg-[#F4F4F8] rounded-xl border border-gray-200 p-4 text-sm max-w-2xl">
        <p className="font-semibold text-[#010E23] mb-1">Reference Number Format</p>
        <code className="text-[#26148C] text-xs">
          PSH-IT-FRM-[5-digit seq]-[RequesterName]-[ServiceItem]
        </code>
        <p className="mt-2 text-xs text-gray-500">
          <span className="font-medium">Example: </span>
          <code className="text-[#010E23]">PSH-IT-FRM-00021-AhmedAl-AccessManagement</code>
        </p>
        <p className="mt-1 text-xs text-gray-400">
          Forms 00001–00020 were pre-approved manual forms. Digital submissions start from 00021.
        </p>
      </div>

      {/* Form Registry Table */}
      <div className="max-w-3xl">
        <h2 className="text-sm font-semibold text-[#010E23] mb-2">Form Registry</h2>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {loading
            ? <div className="py-8 text-center text-xs text-gray-400">Loading…</div>
            : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    {['Code', 'Form Name', 'Prefix', 'Seq Start', 'Current Seq', 'Tickets', ''].map(h => (
                      <th key={h} className="text-left text-xs font-semibold text-gray-400 px-4 py-3">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {forms.length === 0
                    ? <tr><td colSpan={7} className="text-center text-xs text-gray-400 py-8">No form registry entries</td></tr>
                    : forms.map(f => (
                        <tr key={f.id} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="px-4 py-3 font-semibold text-[#26148C] text-xs">{f.formCode}</td>
                          <td className="px-4 py-3 text-xs text-gray-600">{f.formName}</td>
                          <td className="px-4 py-3 font-mono text-[10px] text-gray-500">{f.prefix}</td>
                          <td className="px-4 py-3 text-xs text-gray-500">{String(f.seqStart).padStart(5, '0')}</td>
                          <td className="px-4 py-3">
                            {editCode === f.formCode
                              ? <input type="number" min={1} className={inputSm + ' w-24'} value={seqValue}
                                  onChange={e => setSeqValue(e.target.value)}
                                  onKeyDown={e => { if (e.key === 'Enter') saveSeq(f.formCode); if (e.key === 'Escape') setEditCode(null); }}
                                  autoFocus />
                              : <span className="font-mono text-xs text-[#26148C]">{String(f.seqCurrent).padStart(5, '0')}</span>
                            }
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500">{f.ticketCount}</td>
                          <td className="px-4 py-3">
                            {editCode === f.formCode
                              ? <div className="flex gap-1.5">
                                  <button onClick={() => saveSeq(f.formCode)} disabled={saving}
                                    className="text-[10px] text-violet-600 hover:underline">{saving ? '…' : 'Save'}</button>
                                  <button onClick={() => setEditCode(null)}
                                    className="text-[10px] text-gray-400 hover:underline">Cancel</button>
                                </div>
                              : <button onClick={() => { setEditCode(f.formCode); setSeqValue(String(f.seqCurrent)); }}
                                  className="text-[10px] text-gray-400 hover:text-violet-600">Edit Seq</button>
                            }
                          </td>
                        </tr>
                      ))
                  }
                </tbody>
              </table>
            )
          }
        </div>
      </div>

      {/* ── Audit Log ─────────────────────────────────────────────────────── */}
      <div>
        <h2 className="text-sm font-semibold text-[#010E23] mb-2">Audit Log</h2>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-3">
          <input className={inputSm + ' min-w-[160px]'} placeholder="Filter by action…"
            value={actionFilter} onChange={e => setActionFilter(e.target.value)} />
          <input className={inputSm + ' min-w-[140px]'} placeholder="Resource type…"
            value={resourceFilter} onChange={e => setResourceFilter(e.target.value)} />
          <input type="date" className={inputSm} title="From date"
            value={fromFilter} onChange={e => setFromFilter(e.target.value)} />
          <input type="date" className={inputSm} title="To date"
            value={toFilter} onChange={e => setToFilter(e.target.value)} />
          {(actionFilter || resourceFilter || fromFilter || toFilter) && (
            <button className="text-xs text-gray-400 hover:text-red-500 transition-colors"
              onClick={() => { setActionFilter(''); setResourceFilter(''); setFromFilter(''); setToFilter(''); }}>
              Clear filters
            </button>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {auditLoading
            ? <div className="py-8 text-center text-xs text-gray-400">Loading…</div>
            : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" style={{ minWidth: '700px' }}>
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100">
                        {['Actor', 'Action', 'Resource', 'Resource ID', 'IP', 'Date'].map(h => (
                          <th key={h} className="text-left text-xs font-semibold text-gray-400 px-4 py-2.5">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {auditEntries.length === 0
                        ? <tr><td colSpan={6} className="text-center text-xs text-gray-400 py-8">No audit entries</td></tr>
                        : auditEntries.map(e => (
                            <tr key={e.id} className="border-b border-gray-50 hover:bg-gray-50">
                              <td className="px-4 py-3 text-xs font-medium text-[#010E23]">
                                {e.user?.name ?? <span className="text-gray-300">System</span>}
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-[10px] font-mono font-medium text-violet-700 bg-violet-50 px-1.5 py-0.5 rounded">
                                  {e.action}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-xs text-gray-500">{e.resourceType ?? '—'}</td>
                              <td className="px-4 py-3 font-mono text-[10px] text-gray-400 max-w-[140px] truncate">
                                {e.resourceId ?? '—'}
                              </td>
                              <td className="px-4 py-3 text-[10px] text-gray-300">{e.ipAddress ?? '—'}</td>
                              <td className="px-4 py-3 text-[10px] text-gray-400 whitespace-nowrap">{fmtDT(e.createdAt)}</td>
                            </tr>
                          ))
                      }
                    </tbody>
                  </table>
                </div>

                {/* Audit pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/50">
                  <span className="text-xs text-gray-400">
                    {auditTotal === 0
                      ? 'No entries'
                      : `${(auditPage - 1) * AUDIT_PAGE_SIZE + 1}–${Math.min(auditPage * AUDIT_PAGE_SIZE, auditTotal)} of ${auditTotal}`}
                  </span>
                  <div className="flex gap-1">
                    <button onClick={() => setAuditPage(p => Math.max(1, p - 1))} disabled={auditPage <= 1}
                      className="px-2.5 py-1 text-xs rounded-lg border border-gray-200 text-gray-600 hover:bg-white disabled:opacity-35">
                      ← Prev
                    </button>
                    <button onClick={() => setAuditPage(p => Math.min(auditPages, p + 1))} disabled={auditPage >= auditPages}
                      className="px-2.5 py-1 text-xs rounded-lg border border-gray-200 text-gray-600 hover:bg-white disabled:opacity-35">
                      Next →
                    </button>
                  </div>
                </div>
              </>
            )
          }
        </div>
      </div>
    </div>
  );
}

// ── CustomFieldsPanel ─────────────────────────────────────────────────────────

interface CustomField {
  id: string;
  fieldName: string;
  fieldLabel: string;
  fieldType: string;
  fieldOptions: string | null;
  appliesTo: string;
  isRequired: boolean;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
}

const FIELD_TYPES = ['text', 'textarea', 'select', 'checkbox', 'date', 'number'];
const APPLIES_TO  = ['ticket', 'user'];

const blankField = () => ({
  fieldLabel: '', fieldType: 'text', appliesTo: 'ticket',
  isRequired: false, isActive: true, sortOrder: 0, options: [] as string[],
});

function CustomFieldsPanel() {
  const { toast } = useToast();
  const [fields,    setFields]    = useState<CustomField[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [showForm,  setShowForm]  = useState(false);
  const [form,      setForm]      = useState(blankField());
  const [newOption, setNewOption] = useState('');
  const [saving,    setSaving]    = useState(false);
  const [editId,    setEditId]    = useState<string | null>(null);
  const [togglingId,setTogglingId]= useState<string | null>(null);
  const [deletingId,setDeletingId]= useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    api.get<CustomField[]>('/admin/custom-fields')
      .then(r => setFields(r.data))
      .catch(() => toast('Could not load custom fields', 'error'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.fieldLabel.trim() || !form.fieldType || !form.appliesTo) {
      toast('Label, type and applies-to are required', 'error'); return;
    }
    setSaving(true);
    try {
      const payload = {
        fieldLabel:   form.fieldLabel.trim(),
        fieldType:    form.fieldType,
        fieldOptions: form.fieldType === 'select' ? form.options : undefined,
        appliesTo:    form.appliesTo,
        isRequired:   form.isRequired,
        isActive:     form.isActive,
        sortOrder:    form.sortOrder,
      };
      if (editId) {
        await api.put(`/admin/custom-fields/${editId}`, payload);
        toast('Field updated', 'success');
      } else {
        await api.post('/admin/custom-fields', payload);
        toast('Field created', 'success');
      }
      setShowForm(false); setEditId(null); setForm(blankField()); setNewOption('');
      load();
    } catch (e: any) {
      toast(e?.response?.data?.message ?? 'Failed to save', 'error');
    } finally { setSaving(false); }
  };

  const handleToggle = async (f: CustomField) => {
    setTogglingId(f.id);
    try {
      await api.put(`/admin/custom-fields/${f.id}`, { isActive: !f.isActive });
      toast(`Field ${!f.isActive ? 'activated' : 'deactivated'}`, 'success');
      load();
    } catch { toast('Failed to toggle', 'error'); }
    finally { setTogglingId(null); }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await api.delete(`/admin/custom-fields/${id}`);
      toast('Field deleted', 'success');
      load();
    } catch (e: any) {
      toast(e?.response?.data?.message ?? 'Failed to delete', 'error');
    } finally { setDeletingId(null); }
  };

  const openEdit = (f: CustomField) => {
    let opts: string[] = [];
    try { opts = JSON.parse(f.fieldOptions ?? '[]'); } catch {}
    setForm({ fieldLabel: f.fieldLabel, fieldType: f.fieldType, appliesTo: f.appliesTo, isRequired: f.isRequired, isActive: f.isActive, sortOrder: f.sortOrder, options: opts });
    setEditId(f.id);
    setShowForm(true);
    setNewOption('');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-[#010E23]">Custom Fields</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Fields with "Applies To: ticket" automatically appear in the New Request form.
          </p>
        </div>
        <Button size="sm" onClick={() => { setShowForm(true); setEditId(null); setForm(blankField()); setNewOption(''); }}>
          + Add Field
        </Button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl border border-violet-200 shadow-sm p-5 space-y-4">
          <h3 className="text-sm font-semibold text-[#010E23]">{editId ? 'Edit Field' : 'New Custom Field'}</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Field Label <span className="text-red-500">*</span></label>
              <input className={inp} placeholder="e.g. Cloud Portal Access Type"
                value={form.fieldLabel} onChange={e => setForm(p => ({ ...p, fieldLabel: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Field Type <span className="text-red-500">*</span></label>
              <select className={sel} value={form.fieldType} onChange={e => setForm(p => ({ ...p, fieldType: e.target.value }))}>
                {FIELD_TYPES.map(t => <option key={t} value={t} style={{ textTransform: 'capitalize' }}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Applies To <span className="text-red-500">*</span></label>
              <select className={sel} value={form.appliesTo} onChange={e => setForm(p => ({ ...p, appliesTo: e.target.value }))}>
                {APPLIES_TO.map(a => <option key={a} value={a} style={{ textTransform: 'capitalize' }}>{a}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Sort Order</label>
              <input type="number" min={0} className={inp} value={form.sortOrder}
                onChange={e => setForm(p => ({ ...p, sortOrder: parseInt(e.target.value) || 0 }))} />
            </div>
          </div>

          {form.fieldType === 'select' && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-2">Options</label>
              <div className="space-y-1.5 mb-2">
                {form.options.map((opt, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-xs text-gray-700 flex-1 px-2 py-1 bg-gray-50 rounded">{opt}</span>
                    <button className="text-red-400 hover:text-red-600 text-xs"
                      onClick={() => setForm(p => ({ ...p, options: p.options.filter((_, j) => j !== i) }))}>
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input className={inp + ' flex-1'} placeholder="Add option…" value={newOption}
                  onChange={e => setNewOption(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && newOption.trim()) { setForm(p => ({ ...p, options: [...p.options, newOption.trim()] })); setNewOption(''); }}} />
                <Button size="sm" variant="secondary"
                  onClick={() => { if (newOption.trim()) { setForm(p => ({ ...p, options: [...p.options, newOption.trim()] })); setNewOption(''); }}}>
                  Add
                </Button>
              </div>
            </div>
          )}

          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.isRequired} className="rounded text-violet-600"
                onChange={e => setForm(p => ({ ...p, isRequired: e.target.checked }))} />
              <span className="text-xs text-gray-600">Required</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.isActive} className="rounded text-violet-600"
                onChange={e => setForm(p => ({ ...p, isActive: e.target.checked }))} />
              <span className="text-xs text-gray-600">Active</span>
            </label>
          </div>

          <div className="flex gap-2 pt-1">
            <Button size="sm" disabled={saving} onClick={handleSave}>
              {saving ? 'Saving…' : editId ? 'Update Field' : 'Save Field'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-gray-400 text-center py-8">Loading…</div>
      ) : fields.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 py-10 text-center text-sm text-gray-400">
          No custom fields yet. Click "+ Add Field" to create one.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                {['Label', 'Field Name', 'Type', 'Applies To', 'Required', 'Active', ''].map(h => (
                  <th key={h} className="text-left text-xs font-semibold text-gray-400 px-4 py-2.5">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {fields.map(f => (
                <tr key={f.id} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-medium text-[#010E23]">{f.fieldLabel}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-gray-500">{f.fieldName}</td>
                  <td className="px-4 py-2.5 capitalize">{f.fieldType}</td>
                  <td className="px-4 py-2.5 capitalize">{f.appliesTo}</td>
                  <td className="px-4 py-2.5">{f.isRequired ? <span className="text-red-500 font-medium">Yes</span> : '—'}</td>
                  <td className="px-4 py-2.5">
                    <Toggle value={f.isActive} onChange={() => !togglingId && handleToggle(f)} />
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex gap-2">
                      <button className="text-xs text-violet-600 hover:underline" onClick={() => openEdit(f)}>Edit</button>
                      <button className="text-xs text-red-400 hover:underline" disabled={deletingId === f.id}
                        onClick={() => window.confirm(`Delete field "${f.fieldLabel}"?`) && handleDelete(f.id)}>
                        {deletingId === f.id ? '…' : 'Delete'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Admin ─────────────────────────────────────────────────────────────────────

export default function Admin() {
  const { user: currentUser } = useAuth();
  const { toast }             = useToast();
  const [tab,      setTab]      = useState<Tab>('users');
  const [settings, setSettings] = useState<Setting[]>([]);
  const [loading,  setLoading]  = useState(false);

  useEffect(() => {
    if (tab === 'settings') {
      setLoading(true);
      api.get<Setting[]>('/admin/settings')
        .then(r => setSettings(r.data))
        .finally(() => setLoading(false));
    }
  }, [tab]);

  const batchSaveSetting = async (edits: Record<string, string>) => {
    try {
      for (const [key, value] of Object.entries(edits)) {
        await api.put('/admin/settings', { key, value });
        setSettings(prev => prev.map(s => s.key === key ? { ...s, value } : s));
      }
      toast('Settings saved successfully', 'success');
    } catch (e: any) {
      toast(e?.response?.data?.message ?? 'Failed to save settings', 'error');
    }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'users',       label: 'User Management' },
    { id: 'settings',    label: 'Portal Settings' },
    { id: 'departments', label: 'Departments' },
    { id: 'work-statuses', label: 'Work Statuses' },
    { id: 'forms',         label: 'Form Registry' },
    { id: 'custom-fields', label: 'Custom Fields' },
  ];

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <h1 className="text-lg font-bold text-[#010E23]">Administration</h1>

      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl flex-wrap">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-all ${
              tab === t.id ? 'bg-white shadow-sm text-[#010E23]' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'users' && currentUser && (
        <UsersPanel currentUserId={currentUser.id} />
      )}

      {tab === 'settings' && (
        loading
          ? <div className="text-sm text-gray-400 text-center py-8">Loading…</div>
          : <SettingsPanel settings={settings} onBatchSave={batchSaveSetting} />
      )}

      {tab === 'departments' && <DepartmentsPanel />}
      {tab === 'work-statuses' && <WorkStatusesPanel />}

      {tab === 'forms' && <FormRegistryPanel />}

      {tab === 'custom-fields' && <CustomFieldsPanel />}
    </div>
  );
}
